import * as THREE from 'three';
import type { ExplorationMap, GameDefinition, ProximityInteraction, RoomLayoutDef } from '../../types/game';
import {
  buildWalkGrid, findPath, isWalkable, nearestWalkable, paletteFor,
  buildProp, propFootprint, buildAvatar, animateRig, buildCompanion,
  type WalkGrid, type Palette, type BlockyRig
} from './kit';
import { Character, CHARACTER_MODELS, CHARACTER_HEIGHTS, PLAYER_CHOICES, readPlayerChoice, writePlayerChoice, type PlayerChoice } from './characters';
import { ProceduralSky } from './sky';
import { spawnFauna, updateFauna, type FaunaInstance } from './fauna';

export interface WorldViewCallbacks {
  onInteract: (id: string) => void;
  onBlocked: (hint: string) => void;
  onSequenceSfx?: (sound: string, volume?: number) => void;
  onMapEnter?: (mapId: string) => void;
  onCheckCondition?: (condition: unknown) => boolean;
  onInteractionAvailable?: (id: string) => boolean;
  onInteractionEnabled?: (id: string) => boolean;
  /** Puntos ganados en el mundo (p. ej. al neutralizar un enemigo). */
  onScore?: (points: number, reason: string) => void;
}

/** Enemigo que deambula por la sala: se neutraliza de cerca y suma puntos. */
interface Enemy {
  char: Character;
  home: THREE.Vector3;
  target: THREE.Vector3;
  facing: number;
  speed: number;
  alive: boolean;
  dying: number;
  repath: number;
}

interface Marker {
  id: string;
  group: THREE.Group;
  base: THREE.Vector3;
  gem: THREE.Mesh;
  ring: THREE.Mesh;
  beacon: THREE.Mesh;
  beaconMat: THREE.MeshBasicMaterial;
  badge?: THREE.Sprite;
}

export class WorldView {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private sun: THREE.DirectionalLight | null = null;
  private raf = 0;
  private clock = new THREE.Clock();
  private reducedMotion = false;
  private moveEnabled = true;
  private dialogueFraming = false;

  private room: THREE.Group | null = null;
  private grid: WalkGrid | null = null;
  private palette: Palette | null = null;
  private currentMapId: string | null = null;

  private rig: BlockyRig | null = null;
  private companion: THREE.Group | null = null;
  private sky: ProceduralSky | null = null;
  // Personajes GLB (Poly Pizza). Sustituyen a los bloques cuando terminan de cargar.
  private playerChar: Character | null = null;
  private companionChar: Character | null = null;
  /** Habitantes fijos del mapa (mujer, niño, niña) creados en cada sala. */
  private npcs: Character[] = [];
  private enemies: Enemy[] = [];
  /** Animales sueltos que vagan por la sala (Quaternius CC0). */
  private fauna: FaunaInstance[] = [];
  private playerPos = new THREE.Vector3(2, 0, 2);
  private facing = 0;
  // Cámara orbital: el jugador puede rotar (arrastrar con la rueda/botón derecho)
  // y hacer zoom (girar la rueda), como se espera en un mundo 3D.
  private camYaw = 0;
  private camPitch = 0.78;
  private camDist = 7;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  /** Handler guardado del overlay de personaje, para poder quitarlo en dispose. */
  private pickerEscape: ((ev: KeyboardEvent) => void) | null = null;
  private path: Array<{ x: number; z: number }> = [];
  private keys: Record<string, boolean> = {};
  private portalGuard: string | null = null;
  private visClock = 0;
  private elapsedSec = 0;

  private markers: Marker[] = [];
  private compass: THREE.Group | null = null;
  private portalGroups: Array<{ id: string; group: THREE.Group; pos: THREE.Vector3 }> = [];
  private propByObject = new Map<string, THREE.Group>();
  private propByInteraction = new Map<string, THREE.Group>();
  private disposables: Array<{ dispose: () => void }> = [];

  private raycaster = new THREE.Raycaster();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private ghost: THREE.Mesh | null = null;
  private particles: THREE.Points | null = null;
  /** Cuando el jugador hace clic en un marcador/atril, al llegar se entra solo. */
  private pendingAutoTarget: { x: number; z: number; interactionId?: string } | null = null;

  constructor(
    private readonly parent: HTMLElement,
    private readonly def: GameDefinition,
    private readonly callbacks: WorldViewCallbacks,
    _assetBaseUrl = ''
  ) {}

  create(): void {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.parent.clientWidth || 960, this.parent.clientHeight || 540);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.inset = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.parent.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(55, (this.parent.clientWidth || 960) / (this.parent.clientHeight || 540), 0.1, 300);
    this.camera.position.set(6, 8, 10);

    const hemi = new THREE.HemisphereLight(0xdff3ff, 0x404040, 0.7);
    this.scene.add(hemi);
    this.sun = new THREE.DirectionalLight(0xfff2cc, 1.15);
    this.sun.position.set(14, 22, 10);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.left = -40;
    this.sun.shadow.camera.right = 40;
    this.sun.shadow.camera.top = 40;
    this.sun.shadow.camera.bottom = -40;
    this.scene.add(this.sun);

    // Cielo procedural: skydoma, sol, nubes, estrellas, ciclo día/noche.
    this.sky = new ProceduralSky(180);
    this.scene.add(this.sky.group);

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keydown', this.openCharacterSwitcher);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('pointerup', this.onPointerUp);
    this.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
    this.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
    this.renderer.domElement.addEventListener('wheel', this.onWheel, { passive: false });
    this.renderer.domElement.addEventListener('contextmenu', this.onContextMenu);

    this.showPlayerPicker();
    this.loop();
  }

  /** Orientación nativa de cada modelo para que "mire" hacia +Z local (su frente). */
  private static readonly SPIN = { player: 0, companion: 0, enemy: 0 };

  /**
   * Carga el jugador elegido + el robot de compañía y los coloca en escena.
   * Las ambientaciones (mujer/niño/niña de antes) se eliminaron: el jugador
   * comparte mundo con su robot y la fauna, nadie más.
   */
  private async initCharacters(choice: PlayerChoice): Promise<void> {
    if (!this.scene) return;
    const spec = PLAYER_CHOICES.find((c) => c.id === choice) ?? PLAYER_CHOICES[0];
    try {
      const [player, companion] = await Promise.all([
        Character.load(spec.model, { height: spec.height, spin: WorldView.SPIN.player }),
        Character.load(CHARACTER_MODELS.companion, { height: CHARACTER_HEIGHTS.companion, spin: WorldView.SPIN.companion })
      ]);
      this.playerChar = player;
      this.companionChar = companion;
      this.scene.add(player.group, companion.group);
      player.play('CharacterArmature|Idle');
      companion.play('RobotArmature|Robot_Idle');
      // Oculta las reservas de bloques ahora que hay modelos de calidad.
      if (this.rig) this.rig.group.visible = false;
      if (this.companion) this.companion.visible = false;
    } catch (err) {
      console.warn('[WorldView] personajes GLB no disponibles, se usan bloques:', err);
    }
  }

  /**
   * Overlay de elección de personaje que se muestra al iniciar el mundo 3D.
   * Persiste la elección en localStorage para que arranques con el mismo
   * personaje en próximas sesiones. Se cierra con un click; también puede
   * cerrarse con Escape para re-abrirlo más tarde con la tecla `C`.
   */
  private showPlayerPicker(): void {
    const current = readPlayerChoice();
    const wrap = document.createElement('div');
    wrap.className = 'ae-player-picker';
    wrap.innerHTML = `
      <div class="ae-picker-card">
        <h2>Elige tu personaje</h2>
        <p>Tu robot de compañía te acompañará siempre.</p>
        <div class="ae-picker-row">
          ${PLAYER_CHOICES.map((c) => `
            <button type="button" data-choice="${c.id}" class="ae-picker-btn ${c.id === current ? 'is-current' : ''}">
              <span class="ae-picker-glyph" aria-hidden="true">${glyphFor(c.id)}</span>
              <span class="ae-picker-label">${c.label}</span>
            </button>
          `).join('')}
        </div>
        <p class="ae-picker-hint">Pulsa <kbd>C</kbd> en cualquier momento para cambiar.</p>
      </div>`;
    this.parent.appendChild(wrap);
    const close = (id: PlayerChoice) => {
      wrap.remove();
      writePlayerChoice(id);
      void this.initCharacters(id);
    };
    wrap.addEventListener('click', (ev) => {
      const btn = (ev.target as HTMLElement).closest('[data-choice]') as HTMLElement | null;
      if (!btn) return;
      close(btn.dataset.choice as PlayerChoice);
    });
    // Escape cierra con la elección guardada.
    this.pickerEscape = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        window.removeEventListener('keydown', this.pickerEscape!);
        close(current);
      }
    };
    window.addEventListener('keydown', this.pickerEscape);
  }

  /** Atajo: el usuario puede reabrir el picker con la tecla C. */
  private openCharacterSwitcher = (e: KeyboardEvent): void => {
    if (e.key.toLowerCase() !== 'c') return;
    if (this.pickerEscape) return; // ya está abierto
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    this.showPlayerPicker();
  };

  /* ----------------------------- entrada ----------------------------- */

  private static GAME_KEYS = new Set(['w', 'a', 's', 'd', 'e', ' ', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright']);

  private onKeyDown = (e: KeyboardEvent): void => {
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    const k = e.key.toLowerCase();
    if (!WorldView.GAME_KEYS.has(k)) return;
    this.keys[k] = true;
    if (k === 'e' || k === ' ') { e.preventDefault(); this.tryInteract(); }
    if (k.startsWith('arrow') || k === ' ') e.preventDefault();
  };
  private onKeyUp = (e: KeyboardEvent): void => { this.keys[e.key.toLowerCase()] = false; };

  private onResize = (): void => {
    if (!this.renderer || !this.camera) return;
    const w = this.parent.clientWidth || 960;
    const h = this.parent.clientHeight || 540;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.camera || !this.renderer) return;
    // Botón central (la rueda) o derecho: orbitar la cámara alrededor del jugador.
    if (e.button === 1 || e.button === 2) {
      this.dragging = true;
      this.lastX = e.clientX; this.lastY = e.clientY;
      try { this.renderer.domElement.setPointerCapture(e.pointerId); } catch { /* noop */ }
      e.preventDefault();
      return;
    }
    if (e.button !== 0 || !this.moveEnabled) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(new THREE.Vector2(nx, ny), this.camera);

    // 1) Si el clic acierta un marcador numerado, caminamos hasta él y, al
    //    llegar, se ejecuta ESA interacción (la que el jugador eligió, no la
    //    que el motor decida por él). Así puede repasar el estudio o saltar
    //    al ejercicio libremente; si la interacción está deshabilitada, el
    //    motor muestra su pista ("Primero estudia los apuntes…").
    const markerHits = this.raycaster.intersectObjects(
      this.markers.map((m) => m.group),
      true
    );
    if (markerHits.length > 0) {
      let obj: THREE.Object3D | null = markerHits[0].object;
      while (obj && !obj.userData?.interactionId) obj = obj.parent;
      if (obj?.userData?.interactionId) {
        const id = obj.userData.interactionId as string;
        const marker = this.markers.find((m) => m.id === id);
        if (marker) {
          this.beginClickToEnter(marker.base.x, marker.base.z, id);
          return;
        }
      }
    }

    // 2) Si no, clic en el suelo: caminar al punto. Si el punto cae cerca de
    //    un atril con interacción disponible, también auto-entrar al llegar
    //    eligiendo la mejor (reglas del motor).
    const hit = new THREE.Vector3();
    if (!this.raycaster.ray.intersectPlane(this.groundPlane, hit)) return;
    const propSnap = this.nearestPropWithInteraction(hit.x, hit.z, 1.6);
    this.beginClickToEnter(propSnap?.x ?? hit.x, propSnap?.z ?? hit.z);
  };

  /** Pide caminar al punto y, al llegar, disparar la interacción (o la mejor
   *  disponible si no se concretó una). */
  private beginClickToEnter(x: number, z: number, interactionId?: string): void {
    this.requestMoveTo(x, z);
    if (interactionId) {
      this.pendingAutoTarget = { x, z, interactionId };
    } else if (this.hasInteractionNear(x, z, 2.2)) {
      this.pendingAutoTarget = { x, z };
    } else {
      this.pendingAutoTarget = null;
    }
  }

  /** ¿Hay alguna interacción disponible a menos de `radius` del punto? */
  private hasInteractionNear(x: number, z: number, radius: number): boolean {
    const mapDef = this.currentMap();
    if (!mapDef) return false;
    for (const inter of Object.values(mapDef.interactions ?? {})) {
      if (!this.interactionAvailable(inter)) continue;
      const prop = inter.attachTo ? this.propByObject.get(inter.attachTo) : this.propByInteraction.get(inter.id);
      const px = prop ? prop.position.x : inter.position.x * (mapDef.world?.tilesWide ?? 32);
      const pz = prop ? prop.position.z : inter.position.y * (mapDef.world?.tilesDeep ?? 24);
      if (Math.hypot(px - x, pz - z) <= radius) return true;
    }
    return false;
  }

  /** Posición del prop más cercano al punto, si tiene interacción disponible. */
  private nearestPropWithInteraction(x: number, z: number, radius: number): { x: number; z: number } | null {
    const mapDef = this.currentMap();
    if (!mapDef) return null;
    let best: { x: number; z: number; d: number } | null = null;
    for (const inter of Object.values(mapDef.interactions ?? {})) {
      if (!this.interactionAvailable(inter)) continue;
      const prop = inter.attachTo ? this.propByObject.get(inter.attachTo) : this.propByInteraction.get(inter.id);
      if (!prop) continue;
      const d = Math.hypot(prop.position.x - x, prop.position.z - z);
      if (d <= radius && (!best || d < best.d)) {
        best = { x: prop.position.x, z: prop.position.z, d };
      }
    }
    return best ? { x: best.x, z: best.z } : null;
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX; this.lastY = e.clientY;
    this.camYaw -= dx * 0.006;
    this.camPitch = Math.min(1.45, Math.max(0.12, this.camPitch + dy * 0.005));
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.renderer?.domElement.releasePointerCapture(e.pointerId); } catch { /* noop */ }
  };

  /** Rueda del ratón: zoom de la cámara orbital. */
  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.camDist = Math.min(28, Math.max(5, this.camDist + e.deltaY * 0.012));
  };

  private onContextMenu = (e: Event): void => { e.preventDefault(); };

  /** Pide caminar a un tile; si no hay ruta, responde con un marcador fantasma. */
  requestMoveTo(x: number, z: number): void {
    if (!this.grid) return;
    const tx = Math.round(x);
    const tz = Math.round(z);
    // La celda exacta puede estar bloqueada por el propio mobiliario (p.ej. el
    // atril ocupa 2x1). Si lo está, buscamos la baldosa transitable más
    // cercana en un anillo de 3 — el clic "en el punto" siempre debe poder
    // resolverse en una posición a la que el jugador pueda llegar de verdad.
    let goalX = tx;
    let goalZ = tz;
    if (!isWalkable(this.grid, goalX, goalZ)) {
      const near = nearestWalkable(this.grid, goalX, goalZ, 3);
      if (isWalkable(this.grid, near.x, near.z)) {
        goalX = near.x; goalZ = near.z;
      } else {
        this.showGhost(tx, tz);
        this.callbacks.onBlocked('No hay paso por ahí: prueba rodeando el obstáculo.');
        return;
      }
    }
    const p = findPath(this.grid, this.playerPos.x, this.playerPos.z, goalX, goalZ);
    if (!p) {
      this.showGhost(tx, tz);
      this.callbacks.onBlocked('Ese punto está conectado pero no se puede llegar caminando.');
      return;
    }
    this.path = p.slice(1);
  }

  private showGhost(x: number, z: number): void {
    if (!this.scene) return;
    if (this.ghost) { this.scene.remove(this.ghost); this.ghost.geometry.dispose(); }
    const g = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.1, 0.8), new THREE.MeshBasicMaterial({ color: 0xe63946, transparent: true, opacity: 0.6 }));
    g.position.set(x, 0.06, z);
    this.scene.add(g);
    this.ghost = g;
    setTimeout(() => { if (this.ghost === g && this.scene) { this.scene.remove(g); g.geometry.dispose(); (g.material as THREE.Material).dispose(); this.ghost = null; } }, 900);
  }

  /* --------------------------- carga de sala --------------------------- */

  loadMap(mapId: string, spawnPoint?: string): void {
    const mapDef = this.def.maps?.[mapId];
    if (!mapDef || !this.scene) return;
    this.clearRoom();
    this.currentMapId = mapId;
    this.pendingAutoTarget = null;

    const world = mapDef.world;
    const tilesWide = world?.tilesWide ?? 32;
    const tilesDeep = world?.tilesDeep ?? 24;
    const palette = paletteFor(undefined, world?.kind ?? 'plaza');
    this.palette = palette;

    this.scene.background = new THREE.Color(palette.sky);
    this.scene.fog = new THREE.Fog(palette.fog, 30, 90);
    if (this.sun) {
      this.sun.color.setHex(palette.sun);
      this.sun.intensity = palette.sunIntensity;
    }

    const room = new THREE.Group();
    this.room = room;
    this.scene.add(room);

    // Suelo (baseplate blocky) + parches de variación.
    const base = new THREE.Mesh(new THREE.BoxGeometry(tilesWide, 0.5, tilesDeep), this.lambert(palette.ground));
    base.position.set(tilesWide / 2 - 0.5, -0.25, tilesDeep / 2 - 0.5);
    base.receiveShadow = true;
    room.add(base);

    // Camino diseñado (línea de deseo): se ve por dónde ir sin flechas.
    for (const pt of world?.path ?? []) {
      const tile = new THREE.Mesh(new THREE.BoxGeometry(1, 0.08, 1), this.lambert(palette.path));
      tile.position.set(pt.x, 0.04, pt.z);
      tile.receiveShadow = true;
      room.add(tile);
    }

    // Muro perimetral: el mundo tiene límites legibles.
    const wallMat = this.lambert(palette.wall);
    const mkWall = (w: number, d: number, x: number, z: number) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, 1.6, d), wallMat);
      m.position.set(x, 0.8, z);
      m.castShadow = true; m.receiveShadow = true;
      room.add(m);
    };
    mkWall(tilesWide + 2, 1, tilesWide / 2 - 0.5, -0.5);
    mkWall(tilesWide + 2, 1, tilesWide / 2 - 0.5, tilesDeep - 0.5);
    mkWall(1, tilesDeep + 2, -0.5, tilesDeep / 2 - 0.5);
    mkWall(1, tilesDeep + 2, tilesWide - 0.5, tilesDeep / 2 - 0.5);

    // Props: mobiliario, vegetación, hito. Cada uno aporta su colisión.
    const solids: Array<{ x: number; z: number; w: number; d: number }> = [];
    const placeProp = (role: string, x: number, z: number, yaw: number, scale: number, objectId?: string, interactionId?: string, tint?: number) => {
      const g = buildProp(role, palette, tint ?? 0);
      g.position.set(x, 0, z);
      g.rotation.y = (yaw * Math.PI) / 180;
      g.scale.setScalar(scale);
      room.add(g);
      if (objectId) this.propByObject.set(objectId, g);
      if (interactionId) this.propByInteraction.set(interactionId, g);
      const fp = propFootprint(role);
      solids.push({ x: x - (fp.w * scale) / 2 + 0.5, z: z - (fp.d * scale) / 2 + 0.5, w: fp.w * scale, d: fp.d * scale });
      return g;
    };

    if (world?.landmark) placeProp(world.landmark.role, world.landmark.x, world.landmark.z, world.landmark.yaw ?? 0, world.landmark.scale ?? 1, world.landmark.objectId, world.landmark.interactionId, world.landmark.tint);
    for (const pp of world?.props ?? []) placeProp(pp.role, pp.x, pp.z, pp.yaw ?? 0, pp.scale ?? 1, pp.objectId, pp.interactionId, pp.tint);

    const layout: RoomLayoutDef = mapDef.world ?? { kind: 'plaza', tilesWide, tilesDeep, ground: 'grass', props: [] };
    this.grid = buildWalkGrid(layout, solids.length ? solids : this.solidsFromMap(mapDef, tilesWide, tilesDeep));

    // Jugador y compañero.
    this.rig = buildAvatar(0x2b2d42, 0xf1c27d, palette.accent);
    this.scene.add(this.rig.group);
    this.companion = buildCompanion(palette.metal, palette.glow);
    this.scene.add(this.companion);

    const spawn = this.spawnFor(mapDef, spawnPoint, tilesWide, tilesDeep);
    const safe = nearestWalkable(this.grid, spawn.x, spawn.z);
    this.playerPos.set(safe.x, 0, safe.z);
    this.path = [];

    this.buildMarkers(mapDef, tilesWide, tilesDeep);
    this.buildPortals(mapDef, tilesWide, tilesDeep);
    this.buildAmbience(mapDef, tilesWide, tilesDeep);
    this.buildCompass(palette.accent);
    void this.spawnEnemies(tilesWide, tilesDeep);
    void this.spawnAmbientFauna(tilesWide, tilesDeep, layout.kind);

    this.camera!.position.set(this.playerPos.x + 5, 11, this.playerPos.z + 10);
    this.callbacks.onMapEnter?.(mapId);
  }

  private lambert(color: number): THREE.MeshLambertMaterial {
    const m = new THREE.MeshLambertMaterial({ color });
    this.disposables.push(m);
    return m;
  }

  private solidsFromMap(mapDef: ExplorationMap, w: number, d: number): Array<{ x: number; z: number; w: number; d: number }> {
    const out: Array<{ x: number; z: number; w: number; d: number }> = [];
    for (const c of mapDef.collisions ?? []) {
      out.push({ x: c.x * w, z: c.y * d, w: c.w * w, d: c.h * d });
    }
    return out;
  }

  private spawnFor(mapDef: ExplorationMap, spawnPoint: string | undefined, w: number, d: number): { x: number; z: number } {
    const sp = spawnPoint && mapDef.spawnPoints?.[spawnPoint] ? mapDef.spawnPoints[spawnPoint]
      : Object.values(mapDef.spawnPoints ?? {})[0] ?? { x: 0.1, y: 0.85 };
    return { x: sp.x * w, z: sp.y * d };
  }

  private buildMarkers(mapDef: ExplorationMap, w: number, d: number): void {
    // Pre-pase: contar cuántos marcadores OBJETIVO comparten prop con uno
    // no-objetivo, para repartirlos en un arco y que el "reto" verde no se
    // solape con el "apunte" ámbar del mismo atril. Sin reparto, el jugador
    // lee el punto 1 y no ve el siguiente paso.
    type Slot = { inter: ProximityInteraction; index: number; total: number };
    const objectiveSlots = new Map<string, Slot[]>();
    const allByKey = new Map<string, { inter: ProximityInteraction; objective: boolean }[]>();
    for (const inter of Object.values(mapDef.interactions ?? {})) {
      const prop = inter.attachTo ? this.propByObject.get(inter.attachTo) : this.propByInteraction.get(inter.id);
      const key = prop ? `prop:${inter.attachTo ?? inter.id}` : `free:${inter.id}`;
      const arr = allByKey.get(key) ?? [];
      arr.push({ inter, objective: WorldView.isObjective(inter) });
      allByKey.set(key, arr);
    }
    for (const [key, arr] of allByKey) {
      const hasNonObj = arr.some((a) => !a.objective);
      const objs = arr.filter((a) => a.objective);
      if (!hasNonObj || objs.length === 0) continue;
      const slots: Slot[] = objs.map((a, i) => ({ inter: a.inter, index: i, total: objs.length }));
      objectiveSlots.set(key, slots);
    }

    for (const inter of Object.values(mapDef.interactions ?? {})) {
      const prop = inter.attachTo ? this.propByObject.get(inter.attachTo) : this.propByInteraction.get(inter.id);
      const pos = prop
        ? new THREE.Vector3(prop.position.x, 2.2, prop.position.z)
        : new THREE.Vector3(inter.position.x * w, 2.2, inter.position.y * d);

      // Punto de ejercicio/reto (lanza minijuego o es un mecanismo): verde muy
      // claro y distintivo. El resto (apuntes, vídeo, charlas) va en ámbar.
      const objective = WorldView.isObjective(inter);
      const color = objective ? WorldView.OBJECTIVE_COLOR : this.palette!.glow;

      // Si soy objetivo y comparto atril con un marcador de apuntes, me
      // sitúo en un anillo real alrededor del prop (no pegado encima): el
      // verde debe verse a 2-3 tiles del ámbar para que el jugador entienda
      // que es un punto distinto al que tiene que caminar.
      if (objective && prop) {
        const key = `prop:${inter.attachTo ?? inter.id}`;
        const slots = objectiveSlots.get(key);
        const slot = slots?.find((s) => s.inter === inter);
        if (slot) {
          const radius = 2.5;
          const step = Math.PI * 2 / Math.max(1, slot.total);
          const angle = -Math.PI / 2 + step * slot.index;
          pos.x += Math.cos(angle) * radius;
          pos.z += Math.sin(angle) * radius;
          // A ras de suelo para que se lea como "estación del ejercicio",
          // no como "apunte pegado al atril".
          pos.y = 0.6;
        }
      }

      const group = new THREE.Group();
      const gem = new THREE.Mesh(new THREE.OctahedronGeometry(objective ? 0.36 : 0.26), new THREE.MeshBasicMaterial({ color }));
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(objective ? 0.55 : 0.42, 0.06, 8, 24),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 })
      );
      ring.rotation.x = Math.PI / 2;
      // Haz de luz vertical: hace el punto visible por encima del mobiliario y a distancia.
      const beaconMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: objective ? 0.4 : 0.22, depthWrite: false, side: THREE.DoubleSide });
      const beacon = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 2.2, 10, 1, true), beaconMat);
      beacon.position.y = -1.1;
      group.add(gem, ring, beacon);
      if (inter.badge) group.add(this.makeBadge(inter.badge, color));
      group.position.copy(pos);
      // Etiqueta para que el raycast del clic sepa qué interacción pidió el jugador.
      group.userData.interactionId = inter.id;
      this.scene!.add(group);
      this.disposables.push(beaconMat);
      this.markers.push({ id: inter.id, group, base: pos.clone(), gem, ring, beacon, beaconMat, badge: undefined });
      group.visible = this.interactionAvailable(inter);
    }
  }

  /** Color distintivo del siguiente punto a alcanzar (ejercicio/reto). */
  private static readonly OBJECTIVE_COLOR = 0x33ff88;

  /** Un marcador es "objetivo" cuando lanza un minijuego o es un mecanismo: es el punto a alcanzar. */
  private static isObjective(inter: ProximityInteraction): boolean {
    if (inter.kind === 'mechanism') return true;
    return (inter.actions ?? []).some((a) => a.type === 'startMinigame');
  }

  private makeBadge(text: string, color: number): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    const hex = `#${color.toString(16).padStart(6, '0')}`;
    ctx.beginPath(); ctx.arc(64, 64, 58, 0, Math.PI * 2);
    ctx.fillStyle = hex; ctx.fill();
    ctx.lineWidth = 8; ctx.strokeStyle = '#12210f'; ctx.stroke();
    ctx.fillStyle = '#0d1a10';
    ctx.font = 'bold 72px system-ui, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(text, 64, 68);
    const tex = new THREE.CanvasTexture(canvas);
    const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false }));
    spr.scale.set(1.0, 1.0, 1.0);
    spr.position.y = 1.0;
    this.disposables.push(tex, spr.material);
    return spr;
  }

  private buildPortals(mapDef: ExplorationMap, w: number, d: number): void {
    for (const [id, portal] of Object.entries(mapDef.portals ?? {})) {
      const x = portal.position.x * w;
      const z = portal.position.y * d;
      const g = new THREE.Group();
      const post = this.lambert(this.palette!.stone);
      const l = new THREE.Mesh(new THREE.BoxGeometry(0.3, 2.2, 0.3), post); l.position.set(-0.9, 1.1, 0);
      const r = new THREE.Mesh(new THREE.BoxGeometry(0.3, 2.2, 0.3), post); r.position.set(0.9, 1.1, 0);
      const top = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.3, 0.3), post); top.position.set(0, 2.3, 0);
      const glow = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.8, 0.1), new THREE.MeshBasicMaterial({ color: 0x4cc9f0, transparent: true, opacity: 0.5 }));
      glow.position.set(0, 1.0, 0);
      g.add(l, r, top, glow);
      g.position.set(x, 0, z);
      g.castShadow = true;
      this.scene!.add(g);
      this.portalGroups.push({ id, group: g, pos: new THREE.Vector3(x, 0, z) });
      g.visible = this.conditionMet(portal.visibleIf);
    }
  }

  private buildAmbience(mapDef: ExplorationMap, w: number, d: number): void {
    const ambiance = mapDef.ambiance;
    if (!ambiance || ambiance === 'none' || this.reducedMotion) return;
    const count = 120;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = Math.random() * w;
      pos[i * 3 + 1] = Math.random() * 6 + 0.5;
      pos[i * 3 + 2] = Math.random() * d;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const color = ambiance === 'rain' ? 0x9fb8ff : ambiance === 'fireflies' || ambiance === 'embers' ? 0xffd479 : 0xffffff;
    const m = new THREE.PointsMaterial({ color, size: 0.08, transparent: true, opacity: 0.7 });
    this.particles = new THREE.Points(geo, m);
    this.scene!.add(this.particles);
    this.disposables.push(geo, m);
  }

  /** Flecha-brújula sobre el jugador: siempre señala el siguiente punto a alcanzar. */
  private buildCompass(color: number): void {
    const g = new THREE.Group();
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95, depthTest: false });
    const head = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.5, 4), mat);
    head.rotation.x = Math.PI / 2; // apunta hacia +Z local
    head.position.z = 0.28;
    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.5), mat);
    tail.position.z = -0.1;
    g.add(head, tail);
    g.renderOrder = 999;
    g.visible = false;
    this.scene!.add(g);
    this.disposables.push(mat);
    this.compass = g;
  }

  /**
   * Siembra enemigos (slimes) en puntos transitables lejos del spawn. Dan algo a
   * lo que enfrentarse: deambulan y se neutralizan acercándose, sumando puntos.
   */
  private async spawnEnemies(w: number, d: number): Promise<void> {
    if (!this.grid || !this.scene) return;
    const mapId = this.currentMapId;
    const count = this.reducedMotion ? 0 : Math.max(1, Math.min(3, Math.round((w * d) / 260)));
    if (count === 0) return;
    // Elige puntos transitables lejos del spawn.
    const spots: THREE.Vector3[] = [];
    let guard = 0;
    while (spots.length < count && guard++ < 200) {
      const x = Math.round(2 + Math.random() * (w - 4));
      const z = Math.round(2 + Math.random() * (d - 4));
      if (!isWalkable(this.grid, x, z)) continue;
      if (Math.hypot(x - this.playerPos.x, z - this.playerPos.z) < 6) continue;
      spots.push(new THREE.Vector3(x, 0, z));
    }
    // Cada enemigo es un clon del GLB (ya en caché tras la primera carga).
    await Promise.all(spots.map(async (pos) => {
      let char: Character;
      try {
        char = await Character.load(CHARACTER_MODELS.enemy, { height: 0.9, spin: WorldView.SPIN.enemy });
      } catch (err) {
        console.warn('[WorldView] enemigo GLB no disponible:', err);
        return;
      }
      if (mapId !== this.currentMapId) { char.dispose(); return; } // cambió la sala mientras cargaba
      this.addEnemy(char, pos);
    }));
  }

  private addEnemy(char: Character, pos: THREE.Vector3): void {
    this.scene?.add(char.group);
    char.group.position.copy(pos);
    char.play('MonsterArmature|Idle');
    this.enemies.push({
      char, home: pos.clone(), target: pos.clone(), facing: 0,
      speed: 1.6 + Math.random() * 0.6, alive: true, dying: 0, repath: Math.random() * 2
    });
  }

  /**
   * Llena la sala con fauna CC0: 3 animales terrestres al azar en cualquier
   * cuarto abierto y 1-2 peces sólo cuando el arquetipo es `jardin`
   * (interpretado como "jardín con estanque"). En `sala`/`corredor` no se
   * cargan bestias: el espacio es estrecho y estorbarían el recorrido.
   */
  private async spawnAmbientFauna(w: number, d: number, kind: RoomLayoutDef['kind']): Promise<void> {
    if (!this.scene) return;
    const mapId = this.currentMapId;
    const seed = (w * 31 + d * 17 + (mapId?.length ?? 0) * 7) >>> 0;
    let s = seed || 1;
    const rng = () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 0xffffffff;
    };
    // Los peces quedan desactivados hasta que tengamos props de agua/estanque
    // en el layout; sin superficie donde apoyarlos flotarían en el aire.
    const landCount = (kind === 'plaza' || kind === 'patio' || kind === 'jardin') ? 3 : 0;
    const waterCount = 0;
    if (landCount === 0 && waterCount === 0) return;
    const origin = new THREE.Vector3(w / 2, 0, d / 2);
    try {
      const insts = await spawnFauna(landCount, waterCount, this.scene, origin, rng);
      if (mapId !== this.currentMapId) {
        for (const fa of insts) this.scene.remove(fa.char.group), fa.char.dispose();
        return;
      }
      this.fauna = insts;
    } catch {
      // Si la fauna falla seguimos con la sala: nunca romper la entrada.
    }
  }

  private clearRoom(): void {
    if (this.room && this.scene) { this.scene.remove(this.room); this.room = null; }
    for (const m of this.markers) { this.scene?.remove(m.group); }
    this.markers = [];
    for (const e of this.enemies) { this.scene?.remove(e.char.group); e.char.dispose(); }
    this.enemies = [];
    for (const fa of this.fauna) { this.scene?.remove(fa.char.group); fa.char.dispose(); }
    this.fauna = [];
    for (const npc of this.npcs) { this.scene?.remove(npc.group); npc.dispose(); }
    this.npcs = [];
    if (this.compass && this.scene) { this.scene.remove(this.compass); this.compass = null; }
    for (const p of this.portalGroups) { this.scene?.remove(p.group); }
    this.portalGroups = [];
    if (this.rig && this.scene) { this.scene.remove(this.rig.group); this.rig = null; }
    if (this.companion && this.scene) { this.scene.remove(this.companion); this.companion = null; }
    if (this.particles && this.scene) { this.scene.remove(this.particles); this.particles = null; }
    this.propByObject.clear();
    this.propByInteraction.clear();
    for (const d of this.disposables) d.dispose();
    this.disposables = [];
  }

  /* ---------------------------- bucle principal ---------------------------- */

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, this.clock.getDelta());
    this.elapsedSec += dt;
    this.sky?.update(this.elapsedSec);
    this.updatePlayer(dt);
    this.updateCompanion(dt);
    this.updateNpcs(dt);
    this.updateEnemies(dt);
    updateFauna(this.fauna, dt, this.elapsedSec);
    this.updateCamera(dt);
    this.updateMarkers(dt);
    this.updateParticles(dt);
    this.visClock += dt;
    if (this.visClock >= 0.2) { this.visClock = 0; this.refreshVisibility(); }
    this.checkPortalProximity();
    if (this.renderer && this.scene && this.camera) this.renderer.render(this.scene, this.camera);
  };

  private updatePlayer(dt: number): void {
    if (!this.rig || !this.grid) return;
    let vx = 0; let vz = 0;
    if (this.moveEnabled) {
      if (this.keys['w'] || this.keys['arrowup']) vz -= 1;
      if (this.keys['s'] || this.keys['arrowdown']) vz += 1;
      if (this.keys['a'] || this.keys['arrowleft']) vx -= 1;
      if (this.keys['d'] || this.keys['arrowright']) vx += 1;
    }
    const manual = vx !== 0 || vz !== 0;
    if (manual) this.path = [];
    if (manual) this.pendingAutoTarget = null;

    let moving = manual;
    if (!manual && this.path.length > 0) {
      const wp = this.path[0];
      const dx = wp.x - this.playerPos.x;
      const dz = wp.z - this.playerPos.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.15) { this.path.shift(); }
      else { vx = dx / dist; vz = dz / dist; moving = true; }
    }

    // Clic → caminar → al llegar, entrar solo. Cuando se vacía la ruta, el
    // jugador está en la mejor posición posible para el objetivo; disparamos
    // la interacción más cercana (con el desempate por habilitado, será el
    // siguiente paso). El radio del motor decide si hay algo in‑range.
    if (this.pendingAutoTarget && this.path.length === 0) {
      const target = this.pendingAutoTarget;
      this.pendingAutoTarget = null;
      if (target.interactionId) {
        // El jugador eligió un marcador concreto: respetamos su elección.
        // El motor decide si la abre o muestra la pista de "deshabilitada".
        this.callbacks.onInteract(target.interactionId);
      } else {
        // Clic en el suelo cerca de un atril: dispara la mejor disponible.
        this.tryInteract();
      }
    }

    const speed = 5.2;
    if (moving) {
      const len = Math.hypot(vx, vz) || 1;
      vx /= len; vz /= len;
      this.facing = Math.atan2(vx, vz);
      const nx = this.playerPos.x + vx * speed * dt;
      const nz = this.playerPos.z + vz * speed * dt;
      // Colisión por eje para deslizar contra obstáculos en vez de frenarse.
      if (isWalkable(this.grid, Math.round(nx), Math.round(this.playerPos.z))) this.playerPos.x = nx;
      if (isWalkable(this.grid, Math.round(this.playerPos.x), Math.round(nz))) this.playerPos.z = nz;
    }

    this.rig.group.position.set(this.playerPos.x, 0, this.playerPos.z);
    this.rig.group.rotation.y = this.facing;
    animateRig(this.rig, dt, speed, moving);

    if (this.playerChar) {
      this.playerChar.group.position.set(this.playerPos.x, 0, this.playerPos.z);
      this.playerChar.group.rotation.y = this.facing;
      this.playerChar.play(moving ? 'CharacterArmature|Run' : 'CharacterArmature|Idle');
      this.playerChar.update(dt);
    }
  }

  private updateCompanion(dt: number): void {
    const target = new THREE.Vector3(this.playerPos.x - 1.1, this.companionChar ? 0 : 1.1, this.playerPos.z + 0.6);
    if (this.companionChar) {
      // El robot flota siguiendo al jugador y reproduce su animación de reposo.
      const hover = new THREE.Vector3(target.x, 0, target.z);
      this.companionChar.group.position.lerp(hover, Math.min(1, dt * 3));
      this.companionChar.group.position.y = Math.sin(performance.now() / 500) * 0.08;
      // Mira al jugador en vez de dar la espalda al recorrido.
      const dx = this.playerPos.x - this.companionChar.group.position.x;
      const dz = this.playerPos.z - this.companionChar.group.position.z;
      if (Math.hypot(dx, dz) > 0.25) this.companionChar.group.rotation.y = Math.atan2(dx, dz);
      this.companionChar.update(dt);
      return;
    }
    if (!this.companion) return;
    this.companion.position.lerp(target, Math.min(1, dt * 3));
    this.companion.rotation.y += dt * 1.2;
  }

  /** Los habitantes mujer/niño/niña se mueven alrededor del jugador a ritmo suave. */
  private updateNpcs(dt: number): void {
    if (!this.playerPos) return;
    const offsets: Array<{ dx: number; dz: number; face: number }> = [
      { dx: 0.9, dz: 0.7, face: -0.2 },
      { dx: -1.0, dz: -0.6, face: 0.6 },
      { dx: 0.7, dz: -0.9, face: -0.9 }
    ];
    for (let i = 0; i < this.npcs.length && i < offsets.length; i++) {
      const npc = this.npcs[i];
      const o = offsets[i];
      const goal = new THREE.Vector3(this.playerPos.x + o.dx, 0, this.playerPos.z + o.dz);
      npc.group.position.lerp(goal, Math.min(1, dt * 1.4));
      const dx = this.playerPos.x - npc.group.position.x;
      const dz = this.playerPos.z - npc.group.position.z;
      if (Math.hypot(dx, dz) > 0.25) npc.group.rotation.y = Math.atan2(dx, dz) + o.face;
      npc.update(dt);
    }
  }

  /** Los slimes deambulan cerca de su punto; al acercarse el jugador, se le puede neutralizar. */
  private updateEnemies(dt: number): void {
    if (!this.grid) return;
    for (const e of this.enemies) {
      e.char.update(dt);
      if (!e.alive) {
        e.dying += dt;
        e.char.group.position.y = -e.dying * 0.6; // se hunde al morir
        if (e.dying > 1.2) { this.scene?.remove(e.char.group); e.char.dispose(); }
        continue;
      }
      // Reelige destino cada poco para deambular alrededor de su zona.
      e.repath -= dt;
      if (e.repath <= 0) {
        e.repath = 2 + Math.random() * 3;
        const a = Math.random() * Math.PI * 2;
        const r = 1 + Math.random() * 3;
        const tx = Math.round(e.home.x + Math.cos(a) * r);
        const tz = Math.round(e.home.z + Math.sin(a) * r);
        e.target.set(isWalkable(this.grid, tx, tz) ? tx : e.home.x, 0, isWalkable(this.grid, tx, tz) ? tz : e.home.z);
      }
      const dx = e.target.x - e.char.group.position.x;
      const dz = e.target.z - e.char.group.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 0.2) {
        const nx = e.char.group.position.x + (dx / dist) * e.speed * dt;
        const nz = e.char.group.position.z + (dz / dist) * e.speed * dt;
        if (isWalkable(this.grid, Math.round(nx), Math.round(e.char.group.position.z))) e.char.group.position.x = nx;
        if (isWalkable(this.grid, Math.round(e.char.group.position.x), Math.round(nz))) e.char.group.position.z = nz;
        e.facing = Math.atan2(dx, dz);
        e.char.play('MonsterArmature|Walk');
      } else {
        e.char.play('MonsterArmature|Idle');
      }
      e.char.group.rotation.y = e.facing;
    }
    this.enemies = this.enemies.filter((e) => e.alive || e.dying <= 1.2);
  }

  /** Neutraliza el enemigo más cercano si está a distancia de toque. */
  private zapNearestEnemy(): boolean {
    let best: Enemy | null = null;
    let bestD = Infinity;
    for (const e of this.enemies) {
      if (!e.alive) continue;
      const d = Math.hypot(e.char.group.position.x - this.playerPos.x, e.char.group.position.z - this.playerPos.z);
      if (d < 2.2 && d < bestD) { bestD = d; best = e; }
    }
    if (!best) return false;
    best.alive = false;
    best.char.once('MonsterArmature|Death');
    this.playerChar?.once('CharacterArmature|Punch_Left');
    this.callbacks.onSequenceSfx?.('zap', 0.8);
    this.callbacks.onScore?.(25, '¡Enemigo neutralizado!');
    return true;
  }

  private updateCamera(dt: number): void {
    if (!this.camera) return;
    const dist = this.dialogueFraming ? this.camDist * 0.72 : this.camDist;
    const pitch = this.dialogueFraming ? Math.max(this.camPitch, 0.9) : this.camPitch;
    const target = new THREE.Vector3(this.playerPos.x, 1.2, this.playerPos.z);
    const cp = Math.cos(pitch);
    const desired = new THREE.Vector3(
      target.x + dist * cp * Math.sin(this.camYaw),
      target.y + dist * Math.sin(pitch),
      target.z + dist * cp * Math.cos(this.camYaw)
    );
    this.camera.position.lerp(desired, Math.min(1, dt * 8));
    this.camera.lookAt(target);
  }

  private updateMarkers(dt: number): void {
    const t = performance.now() / 1000;
    const mapDef = this.currentMap();

    // Elegir el siguiente punto a alcanzar: el objetivo disponible más cercano;
    // si no hay ninguno, el marcador disponible más cercano (contenido).
    let primary: Marker | null = null;
    let primaryD = Infinity;
    let fallback: Marker | null = null;
    let fallbackD = Infinity;
    for (const m of this.markers) {
      if (!m.group.visible) continue;
      const d = Math.hypot(m.base.x - this.playerPos.x, m.base.z - this.playerPos.z);
      if (d < fallbackD) { fallbackD = d; fallback = m; }
      const inter = mapDef?.interactions?.[m.id];
      if (inter && WorldView.isObjective(inter) && d < primaryD) { primaryD = d; primary = m; }
    }
    const target = primary ?? fallback;
    const targetD = primary ? primaryD : fallbackD;

    for (const m of this.markers) {
      if (!m.group.visible) { m.group.scale.setScalar(1); continue; }
      const isTarget = m === target;
      const isObj = WorldView.isObjective(mapDef?.interactions?.[m.id] ?? { id: m.id, label: '', position: { x: 0, y: 0 } });
      // Los objetivos laten y crecen; el contenido queda discreto. El punto
      // seleccionado respira más fuerte para dejar claro que es el siguiente.
      const pulse = isTarget ? 1 + Math.sin(t * (isObj ? 5 : 3)) * 0.16 : 1;
      const s = (isObj ? 1.15 : 0.9) * pulse;
      m.group.scale.setScalar(s);
      m.group.position.y = m.base.y + Math.sin(t * 2 + m.base.x) * 0.15;
      m.group.rotation.y += dt * (isTarget ? 2.4 : 1.2);
      m.beaconMat.opacity = isTarget ? 0.55 + Math.sin(t * 5) * 0.2 : isObj ? 0.34 : 0.18;
    }

    // Brújula: flota sobre el jugador y apunta al siguiente punto.
    if (this.compass) {
      if (!target || targetD < 2.2) {
        this.compass.visible = false;
      } else {
        this.compass.visible = true;
        this.compass.position.set(this.playerPos.x, 2.9 + Math.sin(t * 3) * 0.08, this.playerPos.z);
        this.compass.rotation.y = Math.atan2(target.base.x - this.playerPos.x, target.base.z - this.playerPos.z);
      }
    }
  }

  private updateParticles(dt: number): void {
    if (!this.particles) return;
    const attr = this.particles.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < attr.count; i++) {
      let y = attr.getY(i) - dt * 1.2;
      if (y < 0.2) y = 6;
      attr.setY(i, y);
    }
    attr.needsUpdate = true;
  }

  private checkPortalProximity(): void {
    const mapDef = this.currentMap();
    if (!mapDef?.portals) return;
    for (const p of this.portalGroups) {
      const def = mapDef.portals[p.id];
      if (!def || !this.conditionMet(def.visibleIf)) continue;
      const inside = Math.hypot(p.pos.x - this.playerPos.x, p.pos.z - this.playerPos.z) < 1.4;
      if (inside) {
        if (this.portalGuard === def.targetMap) continue;
        this.portalGuard = this.currentMapId;
        this.loadMap(def.targetMap, def.targetSpawn);
        return;
      }
      if (this.portalGuard === def.targetMap) this.portalGuard = null;
    }
  }

  /** Reevalúa marcadores y puertas según el estado: una puerta que se abre al cumplir un objetivo debe aparecer. */
  private refreshVisibility(): void {
    const mapDef = this.currentMap();
    if (!mapDef) return;
    for (const m of this.markers) {
      const inter = mapDef.interactions?.[m.id];
      if (inter) m.group.visible = this.interactionAvailable(inter);
    }
    for (const p of this.portalGroups) {
      const def = mapDef.portals?.[p.id];
      if (def) p.group.visible = this.conditionMet(def.visibleIf);
    }
  }

  /* ------------------------------ interacción ------------------------------ */

  private tryInteract(): void {
    const mapDef = this.currentMap();
    if (!mapDef) return;
    // Si hay un enemigo a distancia de toque, la acción lo neutraliza.
    if (this.zapNearestEnemy()) return;
    const isEnabled = (id: string): boolean => this.callbacks.onInteractionEnabled?.(id) ?? true;
    let best: ProximityInteraction | null = null;
    let bestD = Infinity;
    let bestEnabled = false;
    let bestIsObj = false;
    for (const inter of Object.values(mapDef.interactions ?? {})) {
      if (!this.interactionAvailable(inter)) continue;
      const prop = inter.attachTo ? this.propByObject.get(inter.attachTo) : this.propByInteraction.get(inter.id);
      const px = prop ? prop.position.x : inter.position.x * (mapDef.world?.tilesWide ?? 32);
      const pz = prop ? prop.position.z : inter.position.y * (mapDef.world?.tilesDeep ?? 24);
      const d = Math.hypot(px - this.playerPos.x, pz - this.playerPos.z);
      if (d >= (inter.radius ?? 90) / 30) continue;
      const enabled = isEnabled(inter.id);
      const isObj = WorldView.isObjective(inter);
      // Ganador si está más cerca; en empate (mismo atril) gana el objetivo
      // habilitado, para que tras estudiar se abra el ejercicio y no se
      // re-lean los apuntes.
      const closer = d < bestD - 0.01;
      const sameSpot = Math.abs(d - bestD) <= 0.01;
      const preferred = sameSpot && enabled && !bestEnabled;
      const preferredObj = sameSpot && enabled === bestEnabled && isObj && !bestIsObj;
      if (closer || preferred || preferredObj) {
        best = inter; bestD = d; bestEnabled = enabled; bestIsObj = isObj;
      }
    }
    if (best) this.callbacks.onInteract(best.id);
  }

  private interactionAvailable(inter: ProximityInteraction): boolean {
    if (this.callbacks.onInteractionAvailable) return this.callbacks.onInteractionAvailable(inter.id);
    return this.conditionMet(inter.visibleIf);
  }
  private conditionMet(condition: unknown): boolean {
    if (!condition || !this.callbacks.onCheckCondition) return true;
    return this.callbacks.onCheckCondition(condition);
  }

  private currentMap(): ExplorationMap | undefined {
    return this.currentMapId ? this.def.maps?.[this.currentMapId] : undefined;
  }

  /* --------------------------- API pública (contrato) --------------------------- */

  setMoveEnabled(enabled: boolean): void { this.moveEnabled = enabled; if (!enabled) { this.path = []; this.pendingAutoTarget = null; } }
  setDialogueFraming(active: boolean): void { this.dialogueFraming = active; }
  setReducedMotion(value: boolean): void { this.reducedMotion = value; }

  setObjectState(objectId: string, state: string): void {
    const g = this.propByObject.get(objectId);
    if (!g) return;
    const on = /encendid|abiert|activ|llena|caliente|optimo|moviendo|girando/i.test(state);
    g.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh && (mesh.material as THREE.MeshLambertMaterial).isMeshLambertMaterial) {
        (mesh.material as THREE.MeshLambertMaterial).emissive?.setHex(on ? 0x553300 : 0x000000);
      }
    });
    if (/abiert/i.test(state)) g.rotation.y += 0.6;
  }

  getObjectScreenRect(objectId: string, width: number): { left: number; top: number; width: number; height: number } | null {
    const g = this.propByObject.get(objectId);
    if (!g || !this.camera || !this.renderer) return null;
    const v = g.position.clone(); v.y += 1.5;
    v.project(this.camera);
    const rect = this.renderer.domElement.getBoundingClientRect();
    const host = this.renderer.domElement.parentElement?.getBoundingClientRect() ?? rect;
    const cx = ((v.x + 1) / 2) * rect.width + (rect.left - host.left);
    const cy = ((1 - v.y) / 2) * rect.height + (rect.top - host.top);
    const height = (width * 9) / 16;
    const left = cx - width / 2;
    const top = cy - height / 2;
    if (left + width < 0 || top + height < 0 || left > host.width || top > host.height) return null;
    return { left, top, width, height };
  }

  destroy(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointerup', this.onPointerUp);
    this.renderer?.domElement.removeEventListener('pointerdown', this.onPointerDown);
    this.renderer?.domElement.removeEventListener('pointermove', this.onPointerMove);
    this.renderer?.domElement.removeEventListener('wheel', this.onWheel);
    this.renderer?.domElement.removeEventListener('contextmenu', this.onContextMenu);
    if (this.pickerEscape) {
      window.removeEventListener('keydown', this.pickerEscape);
      this.pickerEscape = null;
    }
    this.clearRoom();
    this.sky?.dispose();
    if (this.sky && this.scene) this.scene.remove(this.sky.group);
    this.sky = null;
    this.renderer?.dispose();
    if (this.renderer && this.renderer.domElement.parentElement === this.parent) this.parent.removeChild(this.renderer.domElement);
    this.renderer = null;
  }
}

/** Glyphs legibles para cada arquetipo de personaje (SVG inline). */
function glyphFor(id: PlayerChoice): string {
  switch (id) {
    case 'man':   return '<svg viewBox="0 0 32 32" width="40" height="40"><circle cx="16" cy="9" r="5" fill="#f1c27d"/><path d="M5 27c1-7 6-10 11-10s10 3 11 10z" fill="#2b2d42"/></svg>';
    case 'woman': return '<svg viewBox="0 0 32 32" width="40" height="40"><circle cx="16" cy="9" r="5" fill="#f1c27d"/><path d="M6 27c1-7 6-11 10-11s9 4 10 11zM9 8c0-4 3-6 7-6s7 2 7 6c-1-2-3-3-7-3s-6 1-7 3z" fill="#2a9d8f"/></svg>';
    case 'boy':   return '<svg viewBox="0 0 32 32" width="40" height="40"><circle cx="16" cy="11" r="4" fill="#f1c27d"/><path d="M7 27c0-5 4-8 9-8s9 3 9 8z" fill="#e76f51"/></svg>';
    case 'girl':  return '<svg viewBox="0 0 32 32" width="40" height="40"><circle cx="16" cy="11" r="4" fill="#f1c27d"/><path d="M7 27c0-5 4-8 9-8s9 3 9 8zM9 10c1-3 4-4 7-4s6 1 7 4l-3 2-4-2-4 2z" fill="#e63946"/></svg>';
  }
}
